/**
 * Time utilities for consistent UTC+7/WIB (Western Indonesian Time) handling
 * 
 * ⚠️ IMPORTANT: Always use these utilities instead of:
 * - new Date().toISOString() (returns UTC)
 * - Manual timezone calculations like now.getTime() + (7 * 60 * 60 * 1000)
 * - Direct Date() constructor without timezone handling
 * 
 * These utilities properly handle timezone conversion and prevent double offset issues.
 */

/**
 * Get current time in WIB (UTC+7) as ISO string
 * Uses proper timezone detection to prevent double offset when system is already UTC+7
 * @returns WIB time in ISO format: "2025-08-07T14:30:45"
 */
export const getWIBTime = (): string => {
  const now = new Date();
  const wibTime = new Date(now.toLocaleString("en-US", {timeZone: WIB_TIMEZONE}));
  return wibTime.toISOString().slice(0, 19); // Format: "2025-08-07T14:30:45"
};

/**
 * Get WIB time as full ISO string with 'Z' suffix for database compatibility
 * Uses proper timezone detection to prevent double offset when system is already UTC+7
 * @returns WIB time in full ISO format: "2025-08-07T14:30:45.123Z"
 */
export const getWIBTimeISO = (): string => {
  const now = new Date();
  const wibTime = new Date(now.toLocaleString("en-US", {timeZone: WIB_TIMEZONE}));
  return wibTime.toISOString();
};

/**
 * Get WIB time for database storage (ISO format without Z)
 * This is the format commonly expected by Directus and other databases
 * @returns WIB time in format: "2025-08-07T14:30:45"
 */
export const getWIBTimeForDB = (): string => {
  return getWIBTime();
};

/**
 * Get WIB time for Directus date_created/date_updated fields
 * Uses the exact format expected by Directus CMS
 * @returns WIB time in Directus format: "2025-08-07T14:30:45.000Z"
 */
export const getWIBTimeForDirectus = (): string => {
  const now = new Date();
  
  // Use Intl.DateTimeFormat to get WIB time components properly
  const formatter = new Intl.DateTimeFormat('en-CA', {
    timeZone: WIB_TIMEZONE,
    year: 'numeric',
    month: '2-digit', 
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false
  });
  
  const parts = formatter.formatToParts(now);
  const year = parts.find(p => p.type === 'year')?.value;
  const month = parts.find(p => p.type === 'month')?.value;
  const day = parts.find(p => p.type === 'day')?.value;
  const hour = parts.find(p => p.type === 'hour')?.value;
  const minute = parts.find(p => p.type === 'minute')?.value;
  const second = parts.find(p => p.type === 'second')?.value;
  
  // Format as ISO string with Z suffix for Directus compatibility
  return `${year}-${month}-${day}T${hour}:${minute}:${second}.000Z`;
};

/**
 * Convert UTC time to WIB time
 * Uses proper timezone conversion to handle all timezone scenarios correctly
 * @param utcTime - UTC time string or Date object
 * @returns WIB time in ISO format
 */
export const convertToWIB = (utcTime: string | Date): string => {
  const date = typeof utcTime === 'string' ? new Date(utcTime) : utcTime;
  const wibTime = new Date(date.toLocaleString("en-US", {timeZone: WIB_TIMEZONE}));
  return wibTime.toISOString().slice(0, 19);
};

/**
 * Convert WIB time to UTC time
 * Uses proper timezone conversion to avoid manual offset calculations
 * @param wibTime - WIB time string (assumes it's already in WIB)
 * @returns UTC time in ISO format
 */
export const convertWIBToUTC = (wibTime: string): string => {
  // Parse the WIB time string and treat it as if it's in Jakarta timezone
  const date = new Date(wibTime + (wibTime.includes('Z') ? '' : 'Z')); // Ensure Z suffix
  // Convert from WIB to UTC by creating a new date with UTC offset
  const utcTime = new Date(date.toLocaleString("en-US", {timeZone: "UTC"}));
  return utcTime.toISOString();
};

/**
 * Format WIB time for display
 * @param wibTime - WIB time string or Date object
 * @param options - Intl.DateTimeFormatOptions
 * @returns Formatted time string
 */
export const formatWIBTime = (
  wibTime: string | Date, 
  options: Intl.DateTimeFormatOptions = {
    year: 'numeric',
    month: '2-digit', 
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false
  }
): string => {
  const date = typeof wibTime === 'string' ? new Date(wibTime) : wibTime;
  return date.toLocaleString('id-ID', options);
};

/**
 * Get current WIB timestamp for filenames
 * @returns Timestamp string: "20250807_143045"
 */
export const getWIBTimestamp = (): string => {
  const wibTime = getWIBTime();
  return wibTime.replace(/[-:]/g, '').replace('T', '_');
};

/**
 * Check if a time string is in WIB format (has timezone offset +07:00 or is already adjusted)
 * @param timeString - Time string to check
 * @returns boolean indicating if it's WIB format
 */
export const isWIBTime = (timeString: string): boolean => {
  return timeString.includes('+07:00') || timeString.includes('WIB');
};

/**
 * Ensure time is in WIB format
 * @param timeString - Input time string (can be UTC or WIB)
 * @returns WIB time string
 */
export const ensureWIBTime = (timeString: string): string => {
  if (isWIBTime(timeString)) {
    return timeString;
  }
  return convertToWIB(timeString);
};

// Constants for time zone
export const WIB_OFFSET_MS = 7 * 60 * 60 * 1000; // 7 hours in milliseconds
export const WIB_TIMEZONE = 'Asia/Jakarta';

/**
 * Get current time with proper WIB timezone using Intl API
 * This is the correct implementation that detects system timezone
 * @returns Date with WIB timezone
 */
export const getCurrentWIBDate = (): Date => {
  return new Date(new Date().toLocaleString("en-US", {timeZone: WIB_TIMEZONE}));
};

/**
 * Format time for Indonesian users with WIB indicator
 * Uses proper timezone formatting to prevent double offset issues
 * @param time - Time string or Date object
 * @returns Formatted string with WIB indicator: "07 Agu 2025, 14:30 WIB"
 */
export const formatForIndonesianUser = (time: string | Date): string => {
  const date = typeof time === 'string' ? new Date(time) : time;
  
  return date.toLocaleString('id-ID', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    timeZone: WIB_TIMEZONE,
    timeZoneName: 'short'
  }).replace('GMT+7', 'WIB');
};

// Export commonly used time functions
export default {
  getWIBTime,
  getWIBTimeISO,
  getWIBTimeForDB,
  getWIBTimeForDirectus,
  convertToWIB,
  convertWIBToUTC,
  formatWIBTime,
  getWIBTimestamp,
  isWIBTime,
  ensureWIBTime,
  getCurrentWIBDate,
  formatForIndonesianUser,
  WIB_OFFSET_MS,
  WIB_TIMEZONE
};